import { Fragment, useState, useEffect, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
  X,
  MessageSquare,
  Play,
  Clock,
  Mail
} from 'lucide-react'
import PropTypes from 'prop-types'
import { apiService } from '../../../services'

const ReplayDialog = ({ chat, onClose, onNewChat }) => {
  const [activeTab, setActiveTab] = useState('chat')
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState(null)
  const [isReplaying, setIsReplaying] = useState(false)
  const [currentReplayIndex, setCurrentReplayIndex] = useState(0)
  const [editingIndex, setEditingIndex] = useState(null)
  const [isEditingName, setIsEditingName] = useState(false)
  const nameInputRef = useRef(null)
  
  const [editedQuestions, setEditedQuestions] = useState(() => {
    return chat.messages.map(msg => msg.question)
  })
  const [isInfoVisible, setIsInfoVisible] = useState(() => {
    return localStorage.getItem('replayInfoVisible') !== 'false'
  })
  const [scheduleConfig, setScheduleConfig] = useState({
    agentName: '',
    startDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    frequency: 'once',
    notifications: {
      email: { enabled: false, recipients: '' },
      slack: { enabled: false, webhook: '' },
      sms: { enabled: false, numbers: '' },
      teams: { enabled: false, webhook: '' }
    }
  })
  const [notificationErrors, setNotificationErrors] = useState({
    email: '',
    slack: '',
    sms: '',
    teams: ''
  })
  const [emailError, setEmailError] = useState('')
  const [agentNameError, setAgentNameError] = useState('')

  useEffect(() => {
    localStorage.setItem('replayInfoVisible', isInfoVisible)
  }, [isInfoVisible])

  const validateEmails = (emailString) => {
    if (!emailString) return true
    const emails = emailString.split(',').map(email => email.trim())
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    
    const invalidEmails = emails.filter(email => !emailRegex.test(email))
    if (invalidEmails.length > 0) {
      return `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}`
    }
    return ''
  }

  const validateAgentName = (name) => {
    if (!name.trim()) return 'Agent name is required';
    if (name.length < 3) return 'Agent name must be at least 3 characters';
    return '';
  };

  const handleEmailChange = (e) => {
    const emailString = e.target.value
    setScheduleConfig(prev => ({ ...prev, email: emailString }))
    setEmailError(validateEmails(emailString))
  }

  const validateNotificationChannel = (channel, value) => {
    if (!value) return ''
    
    switch (channel) {
      case 'email':
        const emails = value.split(',').map(email => email.trim())
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        const invalidEmails = emails.filter(email => !emailRegex.test(email))
        return invalidEmails.length > 0 
          ? `Invalid email${invalidEmails.length > 1 ? 's' : ''}: ${invalidEmails.join(', ')}` 
          : ''
      
      case 'slack':
        return value.startsWith('https://hooks.slack.com/') ? '' : 'Invalid Slack webhook URL'
      
      case 'sms':
        const numbers = value.split(',').map(num => num.trim())
        const phoneRegex = /^\+?[\d\s-]{10,}$/
        const invalidNumbers = numbers.filter(num => !phoneRegex.test(num))
        return invalidNumbers.length > 0
          ? `Invalid phone number${invalidNumbers.length > 1 ? 's' : ''}: ${invalidNumbers.join(', ')}`
          : ''
      
      case 'teams':
        return value.startsWith('https://') ? '' : 'Invalid Teams webhook URL'
      
      default:
        return ''
    }
  }

  const handleNotificationChange = (channel, field, value) => {
    setScheduleConfig(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [channel]: {
          ...prev.notifications[channel],
          [field]: value
        }
      }
    }))

    if (field === 'recipients' || field === 'webhook' || field === 'numbers') {
      setNotificationErrors(prev => ({
        ...prev,
        [channel]: validateNotificationChannel(channel, value)
      }))
    }
  }

  // Function to check if the input bar is ready
  const waitForInputBar = async () => {
    // Wait for up to 20 seconds for the input bar to be ready
    for (let i = 0; i < 40; i++) {
      // Try multiple selectors to find the input element
      const inputElement = 
        document.querySelector('input[type="text"]') || 
        document.querySelector('input[placeholder*="Message"]') ||
        document.querySelector('input[placeholder*="Ask"]') ||
        document.querySelector('form input') ||
        Array.from(document.querySelectorAll('input')).find(el => 
          el.closest('form') || el.closest('[class*="input"]') || el.closest('[class*="chat"]')
        );
      
      if (inputElement) {
        // Try to focus the input element
        try {
          inputElement.focus();
        } catch (error) {
          console.warn('Could not focus input element:', error);
        }
        
        return true;
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    console.warn('Input bar not found after timeout');
    return false;
  };

  // Function to set input value directly via DOM as a fallback
  const setInputValueDirectly = (value) => {
    try {
      // Try multiple selectors to find the input element
      const inputElement = 
        document.querySelector('input[type="text"]') || 
        document.querySelector('input[placeholder*="Message"]') ||
        document.querySelector('input[placeholder*="Ask"]') ||
        document.querySelector('form input') ||
        Array.from(document.querySelectorAll('input')).find(el => 
          el.closest('form') || el.closest('[class*="input"]') || el.closest('[class*="chat"]')
        );
      
      if (inputElement) {
        // Set the value property
        inputElement.value = value;
        
        // Update the internal value property
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype, 'value'
        ).set;
        nativeInputValueSetter.call(inputElement, value);
        
        // Create and dispatch multiple events to ensure React's state is updated
        ['input', 'change', 'keyup'].forEach(eventType => {
          const event = new Event(eventType, { bubbles: true });
          inputElement.dispatchEvent(event);
        });
        
        // Also try to update React's internal state directly if possible
        for (const key in inputElement) {
          if (key.startsWith('__reactProps$')) {
            const props = inputElement[key];
            if (props.onChange) {
              props.onChange({ target: { value } });
            }
            break;
          }
        }
        
        return true;
      } else {
        console.error('Input element not found for direct manipulation');
        return false;
      }
    } catch (error) {
      console.error('Error setting input value directly:', error);
      return false;
    }
  };

  // Function to submit input directly via DOM as a fallback
  const submitInputDirectly = () => {
    try {
      // Try multiple selectors to find the submit button
      const submitButton = 
        document.querySelector('form button[type="submit"]') ||
        document.querySelector('form button') ||
        document.querySelector('button[aria-label*="Send"]') ||
        document.querySelector('button[aria-label*="submit"]') ||
        document.querySelector('button[type="submit"]') ||
        Array.from(document.querySelectorAll('button')).find(btn => {
          const text = btn.textContent.toLowerCase();
          const hasIcon = btn.querySelector('svg');
          return (text.includes('send') || text.includes('submit')) || 
                 (hasIcon && btn.closest('form'));
        });
      
      if (submitButton) {
        // Try multiple ways to trigger the button
        submitButton.click();
        submitButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        
        // Also try to find and submit the form directly
        const form = submitButton.closest('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          
          // Also try to call the onSubmit handler directly
          for (const key in form) {
            if (key.startsWith('__reactProps$')) {
              const props = form[key];
              if (props.onSubmit) {
                props.onSubmit({ preventDefault: () => {} });
              }
              break;
            }
          }
        }
        
        // Also try to call the onClick handler directly if possible
        for (const key in submitButton) {
          if (key.startsWith('__reactProps$')) {
            const props = submitButton[key];
            if (props.onClick) {
              props.onClick();
            }
            break;
          }
        }
        
        return true;
      } else {
        console.error('Submit button not found for direct manipulation');
        
        // Try to find and submit the form directly as a last resort
        const form = document.querySelector('form');
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          
          // Also try to call the onSubmit handler directly
          for (const key in form) {
            if (key.startsWith('__reactProps$')) {
              const props = form[key];
              if (props.onSubmit) {
                props.onSubmit({ preventDefault: () => {} });
              }
              break;
            }
          }
          
          return true;
        }
      }
    } catch (error) {
      console.error('Error submitting input directly:', error);
      return false;
    }
  };

  // Function to create a new chat directly
  const createNewChatDirectly = async () => {
    try {
      console.log('Attempting to create a new chat directly');
      
      // Try to find and click the "New Chat" button
      const newChatButton = 
        document.querySelector('button[aria-label="New Chat"]') || 
        document.querySelector('button[title="New Chat"]') ||
        document.querySelector('button:has(svg[data-testid="PlusIcon"])') ||
        Array.from(document.querySelectorAll('button')).find(btn => {
          const text = (btn.textContent || '').toLowerCase();
          return text.includes('new chat') || text.includes('new thread');
        });
      
      if (newChatButton) {
        console.log('Found New Chat button, clicking it');
        
        // Try multiple ways to trigger the button
        newChatButton.click();
        newChatButton.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        
        // Also try to call the onClick handler directly if possible
        for (const key in newChatButton) {
          if (key.startsWith('__reactProps$')) {
            const props = newChatButton[key];
            if (props.onClick) {
              props.onClick();
            }
            break;
          }
        }
        
        // Wait for the new chat to be created
        await new Promise(resolve => setTimeout(resolve, 2000));
        return true;
      }
      
      // Alternative: Try to create a thread directly using the API service
      console.log('Attempting to create a thread via API service');
      try {
        const threadResponse = await apiService.createThread();
        if (threadResponse && threadResponse.id) {
          console.log('Successfully created new thread:', threadResponse.id);
          
          // Dispatch an event to notify the app about the new thread
          window.dispatchEvent(new CustomEvent('threadCreated', {
            detail: { threadId: threadResponse.id }
          }));
          
          // Also try to dispatch a newChat event
          window.dispatchEvent(new CustomEvent('newChat'));
          
          // Wait for the new chat to be created
          await new Promise(resolve => setTimeout(resolve, 2000));
          return true;
        }
      } catch (apiError) {
        console.error('Error creating thread via API:', apiError);
      }
      
      // If we get here, we couldn't create a new chat
      console.error('Could not create new chat');
      return false;
    } catch (error) {
      console.error('Error creating new chat directly:', error);
      return false;
    }
  };

  const handleRun = async () => {
    try {
      setIsReplaying(true);
      setCurrentReplayIndex(0);
      
      // Create a new chat
      console.log('Creating a new chat...');
      if (typeof onNewChat === 'function') {
        onNewChat();
      } else {
        // Dispatch newChat event
        window.dispatchEvent(new Event('newChat'));
      }
      
      // Wait for thread creation
      console.log('Waiting for thread creation...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Process questions one by one
      if (editedQuestions.length > 0) {
        for (let i = 0; i < editedQuestions.length; i++) {
          // Update the current index
          setCurrentReplayIndex(i);
          
          // Get the current question
          const question = editedQuestions[i];
          console.log(`Processing question ${i + 1}/${editedQuestions.length}: ${question}`);
          
          // Set the input value using the custom event
          console.log('Setting input value via event...');
          window.dispatchEvent(new CustomEvent('setInputValue', {
            detail: { value: question }
          }));
          
          // Wait a moment for the input to be set
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Submit the question using the custom event
          console.log('Submitting question via event...');
          window.dispatchEvent(new CustomEvent('submitInput'));
          
          // Wait for response
          console.log('Waiting for response...');
          
          // Wait for loading state to clear
          let responseReceived = false;
          for (let j = 0; j < 30; j++) {
            // Check for loading indicators
            const isLoading = !!document.querySelector('.loading-indicator') || 
                             !!document.querySelector('[role="progressbar"]') ||
                             !!document.querySelector('[class*="loading"]') ||
                             !!document.querySelector('[class*="spinner"]');
            
            if (!isLoading && j > 5) {
              console.log('Response received (loading indicator gone)');
              responseReceived = true;
              break;
            }
            
            // Wait before checking again
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          
          if (!responseReceived) {
            console.warn('No response detected, continuing anyway');
          }
          
          // Wait before processing the next question
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        // All questions processed
        console.log('All questions processed');
        setIsReplaying(false);
        setCurrentReplayIndex(0);
      } else {
        console.log('No questions to process');
        setIsReplaying(false);
      }
    } catch (error) {
      console.error('Error in handleRun:', error);
      setIsReplaying(false);
      setCurrentReplayIndex(0);
    }
  };

  const handleSchedule = () => {
    // Validate agent name
    const nameError = validateAgentName(scheduleConfig.agentName);
    if (nameError) {
      setAgentNameError(nameError);
      return;
    }

    // Validate all enabled notification channels
    const errors = {}
    Object.entries(scheduleConfig.notifications).forEach(([channel, config]) => {
      if (config.enabled) {
        const value = config.recipients || config.webhook || config.numbers
        const error = validateNotificationChannel(channel, value)
        if (error) errors[channel] = error
      }
    })

    if (Object.keys(errors).length > 0) {
      setNotificationErrors(errors)
      return
    }

    // Create agent configuration with chat history and questions
    const agent = {
      id: Date.now().toString(),
      name: scheduleConfig.agentName,
      schedule: {
        startDate: scheduleConfig.startDate,
        startTime: scheduleConfig.startTime,
        frequency: scheduleConfig.frequency,
      },
      notifications: Object.entries(scheduleConfig.notifications)
        .filter(([_, config]) => config.enabled)
        .reduce((acc, [channel, config]) => ({
          ...acc,
          [channel]: {
            recipients: config.recipients || config.webhook || config.numbers
          }
        }), {}),
      chat: {
        threadId: chat.threadId,
        messages: chat.messages.map((msg, index) => ({
          question: editedQuestions[index],
          originalQuestion: msg.question,
          answer: msg.answer,
          apiResponse: msg.apiResponse
        }))
      },
      createdAt: new Date().toISOString(),
      lastRun: null,
      status: 'scheduled'
    };

    // Save to localStorage
    const agents = JSON.parse(localStorage.getItem('agents') || '[]');
    agents.push(agent);
    localStorage.setItem('agents', JSON.stringify(agents));

    console.log('Agent scheduled:', agent);
    onClose();
  }

  const handleQuestionEdit = (index, newQuestion) => {
    const newQuestions = [...editedQuestions]
    newQuestions[index] = newQuestion
    setEditedQuestions(newQuestions)
  }

  const handleMouseDown = (e) => {
    if (e.target.closest('.dialog-content')) return
    setIsDragging(true)
    const dialog = document.getElementById('replay-dialog')
    const rect = dialog.getBoundingClientRect()
    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
  }

  const handleMouseMove = (e) => {
    if (!isDragging) return
    
    const dialog = document.getElementById('replay-dialog')
    if (dialog) {
      dialog.style.left = `${e.clientX - position.x}px`
      dialog.style.top = `${e.clientY - position.y}px`
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, position])

  // Add focus handler
  const handleNameFocus = () => {
    setIsEditingName(true);
  };

  // Add blur handler
  const handleNameBlur = () => {
    setIsEditingName(false);
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog 
        as="div" 
        className="relative z-50" 
        onClose={onClose}
      >
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

        <div className="fixed inset-0 z-10">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Dialog.Panel 
              id="replay-dialog"
              className="fixed rounded-lg bg-white text-left shadow-xl sm:w-full sm:max-w-4xl h-[36rem]"
              style={{ 
                cursor: isDragging ? 'grabbing' : 'grab',
                position: 'fixed',
                transform: 'none',
                zIndex: 100
              }}
              onMouseDown={handleMouseDown}
            >
              <div className="flex h-full">
                {/* Left Sidebar - Fixed width */}
                <div className="w-64 bg-gray-50 p-4 border-r border-gray-200">
                  <div className="flex items-center mb-6">
                    <h2 className="text-xl font-semibold">Replay</h2>
                  </div>
                  
                  <nav className="space-y-2">
                    <button
                      onClick={() => setActiveTab('chat')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm ${
                        activeTab === 'chat' ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <MessageSquare className="h-5 w-5" />
                      <span>Chat</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('run')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm ${
                        activeTab === 'run' ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Play className="h-5 w-5" />
                      <span>Run</span>
                    </button>
                    <button
                      onClick={() => setActiveTab('schedule')}
                      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm ${
                        activeTab === 'schedule' ? 'bg-gray-200 text-gray-900' : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Clock className="h-5 w-5" />
                      <span>Schedule</span>
                    </button>
                  </nav>
                </div>

                {/* Main Content - Scrollable */}
                <div className="flex-1 flex flex-col relative">
                  <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-400 hover:text-gray-500 z-10"
                  >
                    <X className="h-6 w-6" />
                  </button>

                  <div className="flex-1 overflow-y-auto overflow-x-hidden p-6">
                    {activeTab === 'chat' && (
                      <div className="space-y-4">
                        {chat.messages.map((message, index) => (
                          <div key={index} className="border-b pb-4">
                            <h4 className="font-medium text-gray-900">
                              Question {index + 1}:
                            </h4>
                            {editingIndex === index ? (
                              <textarea
                                value={editedQuestions[index]}
                                onChange={(e) => handleQuestionEdit(index, e.target.value)}
                                onBlur={() => setEditingIndex(null)}
                                autoFocus
                                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                rows={3}
                              />
                            ) : (
                              <p 
                                onClick={() => setEditingIndex(index)}
                                className="mt-1 text-sm text-gray-500 cursor-pointer hover:bg-gray-50 p-2 rounded"
                              >
                                {message.question}
                              </p>
                            )}
                            
                            <h4 className="font-medium text-gray-900 mt-2">Answer:</h4>
                            <p className="mt-1 text-sm text-gray-500">
                              {message.apiResponse?.interpretation?.summary || 
                               message.apiResponse?.summary || 
                               message.answer}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {activeTab === 'run' && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Run Replay</h3>
                        <p className="text-sm text-gray-500 mb-4">
                          Click the button below to start the replay. This will:
                        </p>
                        <ul className="list-disc pl-5 text-sm text-gray-500 mb-4 space-y-1">
                          <li>Create a new chat thread</li>
                          <li>Automatically replay all questions in sequence</li>
                          <li>Show progress in the bar below</li>
                        </ul>
                        <div className="bg-blue-50 p-3 rounded-md mb-4">
                          <p className="text-sm text-blue-700">
                            <strong>Note:</strong> You can keep this dialog open to monitor progress.
                            Each question will be submitted and answered one by one.
                          </p>
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={handleRun}
                            disabled={isReplaying}
                            className={`px-4 py-2 text-sm text-white rounded-md transition-colors ${
                              isReplaying 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                          >
                            {isReplaying ? 'Replay in Progress...' : 'Start Replay'}
                          </button>
                          <button
                            onClick={() => createNewChatDirectly()}
                            disabled={isReplaying}
                            className={`px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors ${
                              isReplaying ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                          >
                            Create New Chat Only
                          </button>
                        </div>
                        
                        {isReplaying && (
                          <div className="mt-6 bg-blue-50 p-4 rounded-md">
                            <div className="flex justify-between mb-2">
                              <span className="text-sm font-medium text-blue-700">
                                Replaying question {currentReplayIndex + 1} of {editedQuestions.length}
                              </span>
                              <span className="text-sm text-blue-700">
                                {Math.round((currentReplayIndex / editedQuestions.length) * 100)}%
                              </span>
                            </div>
                            <div className="h-2 bg-blue-100 rounded-full overflow-hidden">
                              <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${(currentReplayIndex / editedQuestions.length) * 100}%` }}
                              ></div>
                            </div>
                            <p className="mt-2 text-xs text-blue-600">
                              Current question: {editedQuestions[currentReplayIndex]}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'schedule' && (
                      <div className="space-y-6">
                        <h3 className="text-lg font-medium text-gray-900">Schedule Agent</h3>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Agent Name
                            </label>
                            {isEditingName ? (
                              <input
                                ref={nameInputRef}
                                type="text"
                                value={scheduleConfig.agentName}
                                onChange={(e) => {
                                  setScheduleConfig(prev => ({
                                    ...prev,
                                    agentName: e.target.value
                                  }));
                                  setAgentNameError('');
                                }}
                                onFocus={handleNameFocus}
                                onBlur={handleNameBlur}
                                className={`w-full rounded-md border ${
                                  agentNameError ? 'border-red-300' : 'border-gray-300'
                                } px-3 py-2 text-sm`}
                                placeholder="Enter agent name"
                                autoFocus
                              />
                            ) : (
                              <div 
                                onClick={() => {
                                  setIsEditingName(true);
                                  // Focus the input on next render
                                  setTimeout(() => nameInputRef.current?.focus(), 0);
                                }}
                                className="w-full px-3 py-2 text-sm border border-transparent hover:border-gray-300 rounded-md cursor-pointer"
                              >
                                {scheduleConfig.agentName || "Click to add agent name"}
                              </div>
                            )}
                            {agentNameError && (
                              <p className="mt-1 text-sm text-red-600">{agentNameError}</p>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                              </label>
                              <input
                                type="date"
                                value={scheduleConfig.startDate}
                                onChange={(e) => setScheduleConfig(prev => ({
                                  ...prev,
                                  startDate: e.target.value
                                }))}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Time
                              </label>
                              <input
                                type="time"
                                value={scheduleConfig.startTime}
                                onChange={(e) => setScheduleConfig(prev => ({
                                  ...prev,
                                  startTime: e.target.value
                                }))}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Frequency
                            </label>
                            <select
                              value={scheduleConfig.frequency}
                              onChange={(e) => setScheduleConfig(prev => ({
                                ...prev,
                                frequency: e.target.value
                              }))}
                              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            >
                              <option value="once">Once</option>
                              <option value="hourly">Hourly</option>
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Notifications
                            </label>
                            
                            <div className="space-y-4">
                              {/* Email */}
                              <div className="border rounded-md p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={scheduleConfig.notifications.email.enabled}
                                      onChange={(e) => handleNotificationChange('email', 'enabled', e.target.checked)}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="ml-2 text-sm font-medium">Email Notifications</span>
                                  </label>
                                </div>
                                
                                {scheduleConfig.notifications.email.enabled && (
                                  <div>
                                    <input
                                      type="text"
                                      value={scheduleConfig.notifications.email.recipients}
                                      onChange={(e) => handleNotificationChange('email', 'recipients', e.target.value)}
                                      placeholder="Enter email addresses separated by commas"
                                      className={`w-full rounded-md border ${
                                        notificationErrors.email ? 'border-red-300' : 'border-gray-300'
                                      } px-3 py-2 text-sm`}
                                    />
                                    {notificationErrors.email && (
                                      <p className="mt-1 text-sm text-red-600">{notificationErrors.email}</p>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Slack */}
                              <div className="border rounded-md p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={scheduleConfig.notifications.slack.enabled}
                                      onChange={(e) => handleNotificationChange('slack', 'enabled', e.target.checked)}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="ml-2 text-sm font-medium">Slack Notifications</span>
                                  </label>
                                </div>
                                
                                {scheduleConfig.notifications.slack.enabled && (
                                  <div>
                                    <input
                                      type="text"
                                      value={scheduleConfig.notifications.slack.webhook}
                                      onChange={(e) => handleNotificationChange('slack', 'webhook', e.target.value)}
                                      placeholder="Enter Slack webhook URL"
                                      className={`w-full rounded-md border ${
                                        notificationErrors.slack ? 'border-red-300' : 'border-gray-300'
                                      } px-3 py-2 text-sm`}
                                    />
                                    {notificationErrors.slack && (
                                      <p className="mt-1 text-sm text-red-600">{notificationErrors.slack}</p>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* SMS */}
                              <div className="border rounded-md p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={scheduleConfig.notifications.sms.enabled}
                                      onChange={(e) => handleNotificationChange('sms', 'enabled', e.target.checked)}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="ml-2 text-sm font-medium">SMS Notifications</span>
                                  </label>
                                </div>
                                
                                {scheduleConfig.notifications.sms.enabled && (
                                  <div>
                                    <input
                                      type="text"
                                      value={scheduleConfig.notifications.sms.numbers}
                                      onChange={(e) => handleNotificationChange('sms', 'numbers', e.target.value)}
                                      placeholder="Enter phone numbers separated by commas"
                                      className={`w-full rounded-md border ${
                                        notificationErrors.sms ? 'border-red-300' : 'border-gray-300'
                                      } px-3 py-2 text-sm`}
                                    />
                                    {notificationErrors.sms && (
                                      <p className="mt-1 text-sm text-red-600">{notificationErrors.sms}</p>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Teams */}
                              <div className="border rounded-md p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={scheduleConfig.notifications.teams.enabled}
                                      onChange={(e) => handleNotificationChange('teams', 'enabled', e.target.checked)}
                                      className="rounded border-gray-300"
                                    />
                                    <span className="ml-2 text-sm font-medium">Teams Notifications</span>
                                  </label>
                                </div>
                                
                                {scheduleConfig.notifications.teams.enabled && (
                                  <div>
                                    <input
                                      type="text"
                                      value={scheduleConfig.notifications.teams.webhook}
                                      onChange={(e) => handleNotificationChange('teams', 'webhook', e.target.value)}
                                      placeholder="Enter Teams webhook URL"
                                      className={`w-full rounded-md border ${
                                        notificationErrors.teams ? 'border-red-300' : 'border-gray-300'
                                      } px-3 py-2 text-sm`}
                                    />
                                    {notificationErrors.teams && (
                                      <p className="mt-1 text-sm text-red-600">{notificationErrors.teams}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={handleSchedule}
                          className="w-full px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                          disabled={!!Object.values(notificationErrors).some(error => error !== '')}
                        >
                          Schedule Agent
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

ReplayDialog.propTypes = {
  chat: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onNewChat: PropTypes.func // Optional
};

export default ReplayDialog; 